import { useState } from 'react';
import { nip19 } from 'nostr-tools';
import type { NostrEvent } from '@nostrify/nostrify';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { discoveryPool, discoverySignal, DISCOVERY_RELAYS } from '@/lib/appRelays';
import { BUSINESS_KIND, BUSINESS_TAG, parseBusinessEvent, isActiveBusiness } from '@/lib/business';
import { useMerchants } from '@/hooks/useMerchants';
import { Search, Loader2 } from 'lucide-react';

/**
 * Admin-only diagnostic: paste any npub or hex pubkey and see every event that
 * author has published on the relays BCH Local actually reads. This is the
 * ground-truth tool for "listed on BCHNostr but missing here" reports — it
 * shows whether the listing exists on-relay at all, and if so, its exact kind
 * and tags so we can see why it is (or isn't) being imported.
 */
export function MerchantDebugPanel() {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [events, setEvents] = useState<NostrEvent[] | null>(null);
  const [resolvedPubkey, setResolvedPubkey] = useState('');
  const [diagnosis, setDiagnosis] = useState<string[] | null>(null);

  const { merchants } = useMerchants();

  const resolvePubkey = (value: string): string | null => {
    const v = value.trim();
    if (!v) return null;
    if (/^[0-9a-f]{64}$/i.test(v)) return v.toLowerCase();
    try {
      const decoded = nip19.decode(v);
      if (decoded.type === 'npub') return decoded.data;
      if (decoded.type === 'nprofile') return decoded.data.pubkey;
    } catch {
      return null;
    }
    return null;
  };

  const handleLookup = async () => {
    const pubkey = resolvePubkey(input);
    if (!pubkey) {
      setError('Enter a valid npub, nprofile, or 64-char hex pubkey.');
      setEvents(null);
      return;
    }
    setError('');
    setResolvedPubkey(pubkey);
    setLoading(true);
    setEvents(null);
    setDiagnosis(null);
    try {
      const found = await discoveryPool.query(
        [{ authors: [pubkey], limit: 300 }],
        { signal: discoverySignal(AbortSignal.timeout(8000)) },
      );
      found.sort((a, b) => b.created_at - a.created_at);
      setEvents(found);
      setDiagnosis(diagnose(pubkey, found));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Query failed.');
    } finally {
      setLoading(false);
    }
  };

  /** Explain, step by step, whether this pubkey shows on the map and why. */
  const diagnose = (pubkey: string, found: NostrEvent[]): string[] => {
    const notes: string[] = [];

    const onMap = merchants.some(m => m.pubkey === pubkey);
    if (onMap) {
      notes.push('✅ This business is currently on the BCH Local map/list.');
    } else {
      notes.push('⚠️ This business is NOT currently on the map/list.');
    }

    // Look at its latest kind-30499 business profile (if any).
    const biz = found
      .filter(e => e.kind === BUSINESS_KIND && e.tags.some(([n, v]) => n === 't' && v === BUSINESS_TAG))
      .sort((a, b) => b.created_at - a.created_at)[0];

    if (!biz) {
      notes.push('• No kind-30499 business profile found — nothing to import as a BCHNostr business.');
      return notes;
    }

    if (!isActiveBusiness(biz)) {
      notes.push('• Its 30499 profile has empty content → treated as RETIRED and skipped.');
      return notes;
    }

    const parsed = parseBusinessEvent(biz);
    if (!parsed) {
      notes.push('• Its 30499 profile failed to parse (missing name?) → skipped.');
      return notes;
    }

    if (parsed.source === 'bch-local') {
      notes.push(
        '• This is a DIRECT BCH Local listing (source=bch-local). ' +
          'These only appear on the map AFTER an admin approves them in the "Direct listings" tab.',
      );
      if (!onMap) {
        notes.push('👉 Action: approve it in the Direct listings tab to make it visible.');
      } else {
        notes.push('✅ It has been approved, so it is visible.');
      }
    } else {
      notes.push('• This is a native BCHNostr business (source=bchnostr) → shows automatically, no approval needed.');
    }

    return notes;
  };

  const tagsOf = (ev: NostrEvent, name: string) =>
    ev.tags.filter(([n]) => n === name).map(t => t.slice(1).join(':'));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Search className="size-4 text-bch" />
          Listing lookup (debug)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Paste a business's <strong>npub</strong> or hex pubkey to see every event it has
          published on BCH Local's discovery relays. Use this to check why a business listed on
          BCHNostr isn't showing here.
        </p>

        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') handleLookup();
            }}
            placeholder="npub1… or 64-char hex"
            className="flex-1"
          />
          <Button onClick={handleLookup} disabled={loading} className="gap-2">
            {loading ? <Loader2 className="size-4 animate-spin" /> : <Search className="size-4" />}
            Look up
          </Button>
        </div>

        <p className="text-xs text-muted-foreground">
          Reading from: {DISCOVERY_RELAYS.map(r => r.replace('wss://', '')).join(', ')}
        </p>

        {error && <p className="text-sm text-destructive">{error}</p>}

        {diagnosis && (
          <Card className="bg-muted/40">
            <CardContent className="py-4 px-4 space-y-1.5 text-sm">
              {diagnosis.map((line, i) => (
                <p key={i} className={line.startsWith('👉') ? 'font-medium text-bch' : ''}>
                  {line}
                </p>
              ))}
            </CardContent>
          </Card>
        )}

        {events !== null && (
          <div className="space-y-3">
            {events.length === 0 ? (
              <Card className="border-dashed border-destructive/40 bg-destructive/5">
                <CardContent className="py-6 px-4 text-sm">
                  <p className="font-medium text-destructive">
                    No events found for this pubkey on BCH Local's relays.
                  </p>
                  <p className="text-muted-foreground mt-1">
                    The listing does not exist on any relay BCH Local reads. If it shows on
                    bchnostr.com, it likely only lives on a relay we don't query — the business
                    should re-save/re-publish its listing so it propagates, or we add that relay.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <>
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium">{events.length} event(s) found</span>
                  <span className="text-muted-foreground">
                    · kinds: {[...new Set(events.map(e => e.kind))].sort((a, b) => a - b).join(', ')}
                  </span>
                </div>
                <div className="space-y-2">
                  {events.map(ev => {
                    const tTags = tagsOf(ev, 't');
                    const dTag = ev.tags.find(([n]) => n === 'd')?.[1];
                    const nameTag = ev.tags.find(([n]) => n === 'name' || n === 'title')?.[1];
                    return (
                      <div key={ev.id} className="rounded-lg border p-3 text-xs space-y-1.5">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant="secondary" className="font-mono">kind {ev.kind}</Badge>
                          {dTag && <Badge variant="outline" className="font-mono">d={dTag}</Badge>}
                          <span className="text-muted-foreground">
                            {new Date(ev.created_at * 1000).toLocaleString()}
                          </span>
                        </div>
                        {nameTag && <div><span className="text-muted-foreground">name/title:</span> {nameTag}</div>}
                        {tTags.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {tTags.map((t, i) => (
                              <Badge key={i} variant="outline" className="font-mono text-[10px]">
                                t={t}
                              </Badge>
                            ))}
                          </div>
                        )}
                        {ev.content && (
                          <div className="text-muted-foreground break-all line-clamp-3">
                            content: {ev.content.slice(0, 300)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <p className="text-[11px] text-muted-foreground break-all">
                  pubkey: {resolvedPubkey}
                </p>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
